import { Link } from 'react-router-dom';

interface ArticleCardProps {
  article: {
    slug: string;
    title: string;
    excerpt: string;
    category: string;
    publishedAt: string;
    readTime: string;
    imageSeq: string;
  };
}

const categoryNames: Record<string, string> = {
  'zuixin-dizhi': '最新地址',
  'xianlu-gengxin': '线路更新',
  'shiyong-zhinan': '使用指南',
  'changjian-wenti': '常见问题',
  'anquan-fanghu': '安全防护',
};

const articleImages: Record<string, string> = {
  'article-addr-01': 'https://readdy.ai/api/search-image?query=Warm%20minimal%20abstract%20background%20with%20soft%20rose%20and%20golden%20gradients%2C%20clean%20aesthetic%2C%20no%20text%2C%20no%20people%2C%20editorial%20style%2C%20calm%20atmosphere&width=800&height=450&seq=article-addr-01&orientation=landscape',
  'article-addr-02': 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20composition%20with%20warm%20coral%20and%20amber%20tones%2C%20soft%20flowing%20shapes%2C%20minimalist%20aesthetic%2C%20no%20text%2C%20editorial%20quality%2C%20gentle%20gradient&width=800&height=450&seq=article-addr-02&orientation=landscape',
  'article-guide-01': 'https://readdy.ai/api/search-image?query=Soft%20warm%20peach%20and%20cream%20gradient%20background%20with%20subtle%20organic%20textures%2C%20minimal%20clean%20design%2C%20no%20text%2C%20elegant%20editorial%20style%2C%20calm%20atmosphere&width=800&height=450&seq=article-guide-01&orientation=landscape',
  'article-mobile-01': 'https://readdy.ai/api/search-image?query=Gentle%20rosy%20pink%20and%20warm%20beige%20abstract%20background%2C%20soft%20diffused%20gradients%2C%20minimalist%20design%2C%20no%20text%2C%20clean%20editorial%20aesthetic%2C%20peaceful%20mood&width=800&height=450&seq=article-mobile-01&orientation=landscape',
  'article-fix-01': 'https://readdy.ai/api/search-image?query=Warm%20golden%20and%20blush%20abstract%20gradient%2C%20soft%20flowing%20organic%20lines%2C%20minimalist%20elegant%20design%2C%20no%20text%2C%20editorial%20photography%20style%2C%20harmonious%20composition&width=800&height=450&seq=article-fix-01&orientation=landscape',
  'article-safety-01': 'https://readdy.ai/api/search-image?query=Subtle%20warm%20terracotta%20and%20cream%20abstract%20background%2C%20gentle%20shapes%2C%20minimalist%20aesthetic%2C%20no%20text%2C%20clean%20editorial%20style%2C%20soft%20lighting&width=800&height=450&seq=article-safety-01&orientation=landscape',
  'article-features-01': 'https://readdy.ai/api/search-image?query=Delicate%20rose%20and%20golden%20honey%20abstract%20gradient%2C%20soft%20organic%20curves%2C%20minimal%20elegant%20design%2C%20no%20text%2C%20editorial%20quality%2C%20peaceful%20vibe&width=800&height=450&seq=article-features-01&orientation=landscape',
  'article-register-01': 'https://readdy.ai/api/search-image?query=Warm%20apricot%20and%20soft%20pink%20abstract%20background%2C%20gentle%20flowing%20shapes%2C%20minimalist%20aesthetic%2C%20no%20text%2C%20clean%20editorial%20style%2C%20harmonious%20tones&width=800&height=450&seq=article-register-01&orientation=landscape',
  'article-bookmark-01': 'https://readdy.ai/api/search-image?query=Soft%20coral%20and%20warm%20sand%20abstract%20gradient%2C%20subtle%20organic%20textures%2C%20minimalist%20design%2C%20no%20text%2C%20editorial%20aesthetic%2C%20calm%20atmosphere&width=800&height=450&seq=article-bookmark-01&orientation=landscape',
  'article-changelog-01': 'https://readdy.ai/api/search-image?query=Elegant%20amber%20and%20blush%20abstract%20composition%2C%20soft%20diffused%20gradients%2C%20minimal%20clean%20design%2C%20no%20text%2C%20editorial%20quality%2C%20gentle%20lighting&width=800&height=450&seq=article-changelog-01&orientation=landscape',
  'article-contact-01': 'https://readdy.ai/api/search-image?query=Warm%20peach%20and%20cream%20abstract%20background%20with%20subtle%20shapes%2C%20minimalist%20elegant%20design%2C%20no%20text%2C%20editorial%20style%2C%20peaceful%20calm%20mood&width=800&height=450&seq=article-contact-01&orientation=landscape',
  'article-beginner-01': 'https://readdy.ai/api/search-image?query=Soft%20rose%20gold%20and%20warm%20beige%20abstract%20gradient%2C%20gentle%20organic%20flows%2C%20minimalist%20aesthetic%2C%20no%20text%2C%20clean%20editorial%20quality%2C%20harmonious%20composition&width=800&height=450&seq=article-beginner-01&orientation=landscape',
};

export default function ArticleCard({ article }: ArticleCardProps) {
  const imageUrl = articleImages[article.imageSeq] || articleImages['article-addr-01'];

  return (
    <Link
      to={`/article/${article.slug}`}
      className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-secondary-300 transition-all group cursor-pointer"
    >
      <div className="aspect-[16/9] overflow-hidden bg-background-200">
        <img
          src={imageUrl}
          alt={article.title}
          title={`${article.title} - 爱情岛论坛网址导航`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">
            {categoryNames[article.category] || article.category}
          </span>
          <span className="text-xs text-foreground-400 whitespace-nowrap">
            {article.readTime}
          </span>
        </div>
        <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <p className="text-xs text-foreground-400">
          {article.publishedAt}
        </p>
      </div>
    </Link>
  );
}